/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2018
 */

/*global angular*/
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EnumsService } from '../../../../../services/enum.service';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { SuccessService } from '../../../../../services/success.service';
import { UserService } from '../../../../../services/user.service';
import { tap, catchError, of } from 'rxjs';
import { NexleaderEcourseRaliVideoComponent } from '../../../../ecourse/components/ecourse-rali-video/ecourse-rali-video.component';
import { NexleaderOnboardingStrengthsComponent } from '../../onboarding/onboarding-preassessments/onboarding-strengths/onboarding-strengths.component';
import { NexleaderPreIpsatChecklistComponent } from '../../onboarding/onboarding-preassessments/pre-ipsat-checklist/pre-ipsat-checklist.component';
import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-nexleader-ecourse-module-three',
  standalone: true,
  imports: [CommonModule, NexleaderEcourseRaliVideoComponent, NexleaderOnboardingStrengthsComponent, NexleaderPreIpsatChecklistComponent],
  templateUrl: './ecourse-module-three.component.html'
})
export class NexleaderEcourseModuleThreeComponent implements OnInit {
  /**
   * handleNextStep
   *
   * Allows data that is edited on this ecourse module to be saved.
   */
  @Output() handleNextStep = new EventEmitter();

  /**
   * user
   *
   * A user object we can reference to and update as the onboarding user goes throughout the process.
   */
  @Input() user: any;

  currentPrerequisite: any;
  onboardingExperience: any;

  /**
   * moduleSteps
   * 
   * Controls the steps the user can move between for the ecourse module.
   */
  moduleSteps = ['engagement', 'assessment'];

  /**
   * currentStep
   * 
   * Determines the current step the user is on in the ecourse module.
   */
  currentStep = this.moduleSteps[0];

  constructor(
    private userService: UserService,
    private successService: SuccessService,
    private errorHandlerService: ErrorHandlerService,
    private enumsService: EnumsService
  ) { }

  ngOnInit() {
    this.load();
    this.onboardingExperience = this.user.onboardingExperience;

    // Automatically move the user to the engagement step if they've already completed the strengths
    if (this.user && this.user.prerequisites && this.user.prerequisites.completedStrengths) {
      this.nextStep();
    }
  }

  /**
   * load()
   * 
   * Loads all required enums for the module.
   */
  load() {
    this.enumsService.getEnums().pipe(
      tap((enums) => {
        this.currentPrerequisite = enums.Prerequisites.STRENGTHS;
      }), catchError((error) => {
        this.errorHandlerService.handleError(error);
        return of(null);
      })
    ).subscribe();
  }

  /**
   * nextStep()
   * 
   * Handles moving the user to the next step (WITHIN THE MODULE).
   *  Calls the handleNextStep() function if the user is on the last step.
   *  ie: moving from learning_assessment to engagement
   */
  nextStep() {
    const currentStepIndex = this.moduleSteps.indexOf(this.currentStep);
    if (currentStepIndex < this.moduleSteps.length - 1) {
      this.currentStep = this.moduleSteps[currentStepIndex + 1];
    } else {
      this.save();
      this.handleNextStep.emit();
    }
  }

  /**
   * mapContinueButtonTextForStep()
   * 
   * Maps text for the continue buttons given the current step of the module the user
   *  is on.
   */
  mapContinueButtonTextForStep() {
    switch (this.currentStep) {
      case 'engagement':
        return 'Continue to Clifton Strengths';
      default:
        return null;
    }
  }

  /**
   * save()
   * 
   * Saves a user. To be called when an MBTI option is selected.
   */
  save(): void {
    this.userService
      .saveUser(this.user)
      .pipe(
        tap((_) => {
          this.successService.handle();
        }),
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * canProceedToNextStep()
   * 
   * Determines if the user is able to continue to the next module of the ecourse.
   */
  canProceedToNextStep() {
    return this.user
      && this.user.prerequisites
      && this.user.prerequisites.completedStrengths
      && this.user.prerequisites.strengths
      && this.user.prerequisites.strengths.length === 5
      && this.user.prerequisites.strengths.every(this.nonemptyStringObj);
  }

  /**
   * nonemptyStringObj()
   * 
   * helper function
   * 
   * Determines if the object contains a key of name that has a string value.
   * 
   * @param {Object} strO - An object to check.
   */
  nonemptyStringObj(strO: any) {
    if (!strO) return false;
    return typeof strO.name === 'string' && strO.name.trim() !== '';
  }
}
